import { auth, functionsBaseUrl } from '@/firebase';

export type UploadKind = 'runnitUpload' | 'mask' | 'avatar';
export type UploadType = 'IMG' | 'VID';

type UploadInitResponse = {
	success: boolean
	alreadyExists: boolean
	record?: Record<string, any>
	sessionId?: string
	uploadUrl?: string
	objectPath?: string
	headers?: Record<string, string>
	message?: string
};

type UploadFinalizeResponse = {
	success: boolean
	record?: Record<string, any>
	message?: string
};

export type UploadMediaStage = 'auth' | 'init' | 'put' | 'finalize';

/**
 * What kind of thing went wrong, as opposed to where.
 *
 * `status === null` is not enough to classify: fetch rejecting, a 200 with no
 * sessionId, and a 200 with no record all produce one, and only the first is a
 * network problem. Telling a user to check their connection because our own
 * function answered with the wrong shape sends them to fix nothing.
 */
export type UploadMediaReason =
	| 'AUTH'              // no signed-in user, or the ID token would not mint
	| 'TRANSPORT'         // fetch rejected or the request timed out: offline, proxy, CORS
	| 'HTTP'              // the service answered and refused; see status
	| 'INVALID_RESPONSE'; // the service answered 2xx with a body we cannot use

/**
 * A failure on the signed-URL path, carrying enough for a caller to say what
 * went wrong.
 *
 * The three requests behind one uploadMedia call fail for different reasons and
 * need different words: init refuses a file, the PUT loses the network, and
 * finalize disagrees with GCS about what landed. A bare Error collapses all of
 * them, which is what IMG-2026-025 spent its effort undoing on the POST path.
 */
export class UploadMediaError extends Error {
	readonly reason: UploadMediaReason;
	readonly stage: UploadMediaStage;
	readonly status: number | null;
	/** Verbatim `message` from our own function's JSON, when it sent one. */
	readonly serverMessage: string;
	/**
	 * True only when serverMessage came from our function's
	 * `{ success: false, message }` shape. A gateway or CDN error page is
	 * arbitrary HTML, or JSON of its own schema: keep it for logs, never render
	 * it.
	 */
	readonly isTrustedServerMessage: boolean;

	constructor (
		reason: UploadMediaReason,
		stage: UploadMediaStage,
		message: string,
		options?: {
			status?: number | null,
			serverMessage?: string,
			isTrustedServerMessage?: boolean,
			cause?: unknown,
		},
	) {
		super(message);
		// ES5 target drops the prototype chain when extending a built-in, which
		// silently breaks instanceof at the call site. See UploadImageError.
		Object.setPrototypeOf(this, UploadMediaError.prototype);
		this.name = 'UploadMediaError';
		this.reason = reason;
		this.stage = stage;
		this.status = options?.status ?? null;
		this.serverMessage = options?.serverMessage || '';
		this.isTrustedServerMessage = !!options?.isTrustedServerMessage;
		// `cause` is not in the ES2015 Error signature this target uses.
		if (options?.cause !== undefined) {
			(this as Error & { cause?: unknown }).cause = options.cause;
		}
	}
}

/** Small JSON exchanges. Generous for a slow link, bounded so a proxy that accepts and never answers surfaces as an error. */
const JSON_REQUEST_TIMEOUT_MS: number = 60000;
/**
 * The signed PUT streams the whole file. This exists to catch a connection that
 * is dead rather than slow, so it has to sit far above any real transfer: this
 * path also carries the 100MiB video uploads that have shipped with no client
 * timeout at all, and 600s would have aborted a 100MB upload on anything under
 * ~1.4 Mbps upstream, which plenty of home connections are. 30 minutes clears
 * 100MB at well under 0.5 Mbps.
 *
 * Deliberately longer than the 15 minute signed-URL expiry: a V4 signed URL is
 * validated when the request starts, not for the life of the transfer, so a PUT
 * that began in time is not killed at the 15 minute mark.
 */
const SIGNED_PUT_TIMEOUT_MS: number = 1800000;

/** Bound on retained body text, so a full HTML error page never reaches a log line. Matches the POST path. */
const MAX_RETAINED_ERROR_BODY_CHARS: number = 300;

/**
 * The envelope both upload functions reply with, on success and on refusal
 * alike. `success` is always present and always boolean.
 *
 * The guard requires it. A gateway or WAF that happens to answer
 * `{"message": "blocked by policy"}` has no `success`, so it fails this check
 * and its text is captured for logs but never rendered: it is arbitrary
 * third-party prose, not a sentence we wrote for a snackbar.
 */
type UploadFunctionEnvelope = { success: boolean, message?: string };

const isUploadFunctionEnvelope = (value: unknown): value is UploadFunctionEnvelope => {
	if (typeof value !== 'object' || value === null) return false;
	const candidate = value as { success?: unknown, message?: unknown };
	if (typeof candidate.success !== 'boolean') return false;
	return candidate.message === undefined || typeof candidate.message === 'string';
};

const isUploadInitBody = (
	envelope: UploadFunctionEnvelope,
): envelope is UploadFunctionEnvelope & UploadInitResponse => {
	const candidate = envelope as { alreadyExists?: unknown, record?: unknown, sessionId?: unknown, uploadUrl?: unknown };
	// A dedup hit is a complete answer with no session in it, so the two shapes
	// are checked separately rather than as one optional blob.
	if (candidate.alreadyExists === true) {
		return typeof candidate.record === 'object' && candidate.record !== null;
	}
	return typeof candidate.sessionId === 'string' && !!candidate.sessionId
		&& typeof candidate.uploadUrl === 'string' && !!candidate.uploadUrl;
};

const isUploadFinalizeBody = (
	envelope: UploadFunctionEnvelope,
): envelope is UploadFunctionEnvelope & UploadFinalizeResponse => {
	const record: unknown = (envelope as { record?: unknown }).record;
	return typeof record === 'object' && record !== null;
};

const getAuthHeader = async (apiKey?: string): Promise<string | null> => {
	if (apiKey) return `Bearer ${apiKey}`;
	const user = auth?.currentUser || null;
	if (!user) return null;
	try {
		const token = await user.getIdToken();
		return token ? `Bearer ${token}` : null;
	} catch (err: unknown) {
		throw new UploadMediaError('AUTH', 'auth', 'Could not refresh your session token', { cause: err });
	}
};

const toTransportError = (err: unknown, stage: UploadMediaStage, timeoutMs: number): UploadMediaError => {
	// fetch and the body read only reject on a transport-level failure, and the
	// browser deliberately hides which one. An abort is the one case we can name,
	// because we caused it.
	const timedOut: boolean = (err as { name?: string } | null)?.name === 'AbortError';
	return new UploadMediaError(
		'TRANSPORT',
		stage,
		timedOut
			? `Upload timed out after ${Math.round(timeoutMs / 1000)}s`
			: 'Could not reach the upload service',
		{ cause: err },
	);
};

const postJson = async (
	url: string,
	payload: Record<string, unknown>,
	stage: UploadMediaStage,
	apiKey?: string,
): Promise<UploadFunctionEnvelope> => {
	const authHeader = await getAuthHeader(apiKey);
	const headers: Record<string, string> = { 'Content-Type': 'application/json' };
	if (authHeader) {
		headers.Authorization = authHeader;
	}

	const controller: AbortController = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), JSON_REQUEST_TIMEOUT_MS);
	let response: Response;
	let bodyText: string = '';
	try {
		response = await fetch(url, {
			method: 'POST',
			headers,
			body: JSON.stringify(payload),
			signal: controller.signal,
		});
		// Read inside the timeout on purpose. fetch settles once the headers land,
		// so a response whose body never finishes would otherwise hang past every
		// bound we set.
		bodyText = await response.text();
	} catch (err: unknown) {
		throw toTransportError(err, stage, JSON_REQUEST_TIMEOUT_MS);
	} finally {
		clearTimeout(timeoutId);
	}

	let parsed: unknown = null;
	try {
		parsed = JSON.parse(bodyText);
	} catch {
		// Not JSON: an intercepting proxy or the platform's own error page.
	}
	const envelope: UploadFunctionEnvelope | null = isUploadFunctionEnvelope(parsed) ? parsed : null;
	const serverMessage: string = envelope?.message?.trim() || '';

	if (!response.ok) {
		throw new UploadMediaError(
			'HTTP',
			stage,
			serverMessage || `Upload ${stage} failed with status ${response.status}`,
			{
				status: response.status,
				// Trusted only when the full envelope validated. Otherwise keep the
				// raw body for logs, bounded, and never render it.
				serverMessage: serverMessage || bodyText.trim().slice(0, MAX_RETAINED_ERROR_BODY_CHARS),
				isTrustedServerMessage: !!serverMessage,
			},
		);
	}

	if (!envelope || envelope.success !== true) {
		// A 2xx whose body is not ours, or that says success:false, which our
		// functions never pair with a 2xx. Not a network problem and not a refusal
		// we can explain: an answer we cannot use.
		throw new UploadMediaError(
			'INVALID_RESPONSE',
			stage,
			`Upload ${stage} returned an unusable response`,
			{ status: response.status, serverMessage: bodyText.trim().slice(0, MAX_RETAINED_ERROR_BODY_CHARS) },
		);
	}
	return envelope;
};

const putSignedUpload = async (uploadUrl: string, file: File, contentType?: string): Promise<void> => {
	const controller: AbortController = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), SIGNED_PUT_TIMEOUT_MS);
	let response: Response;
	try {
		response = await fetch(uploadUrl, {
			method: 'PUT',
			headers: {
				'Content-Type': contentType || file.type || 'application/octet-stream',
			},
			body: file,
			signal: controller.signal,
		});
	} catch (err: unknown) {
		throw toTransportError(err, 'put', SIGNED_PUT_TIMEOUT_MS);
	} finally {
		clearTimeout(timeoutId);
	}
	if (!response.ok) {
		throw new UploadMediaError('HTTP', 'put', 'Failed to upload file', { status: response.status });
	}
};

export const uploadMedia = async (params: {
	kind: UploadKind
	file: File
	hashHex: string
	uploadType?: UploadType
	useCase?: string | null
	width?: number | null
	height?: number | null
	duration?: number | null
	contentType?: string | null
	teamId?: string | null
	apiKey?: string | null
	baseUrl?: string | null
}): Promise<Record<string, any>> => {
	const {
		kind,
		file,
		hashHex,
		uploadType,
		useCase,
		width,
		height,
		duration,
		contentType: requestedContentType,
		teamId,
		apiKey,
		baseUrl,
	} = params;

	// Mirror the POST path: without a signed-in user getAuthHeader returns null
	// and the request goes out unauthenticated, so the failure would surface as
	// an opaque 401 from init rather than "your session expired".
	if (!apiKey && !auth?.currentUser) {
		throw new UploadMediaError('AUTH', 'auth', 'User must be authenticated to upload');
	}

	// One value for the init payload and the signed PUT header. They must match
	// or uploadMediaFinalize's content-type check rejects the upload.
	const contentType: string = requestedContentType || file.type || 'application/octet-stream';

	const endpointBase = baseUrl || functionsBaseUrl;
	const initPayload = {
		kind,
		hashHex,
		contentType,
		size: file.size,
		width: width || null,
		height: height || null,
		duration: duration || null,
		fileName: file.name || null,
		uploadType: uploadType || null,
		useCase: useCase || null,
		teamId: teamId || null,
	};

	const initEnvelope: UploadFunctionEnvelope = await postJson(
		`${endpointBase}/uploadMediaInit`,
		initPayload,
		'init',
		apiKey || undefined,
	);
	if (!isUploadInitBody(initEnvelope)) {
		throw new UploadMediaError('INVALID_RESPONSE', 'init', 'Upload session creation returned no session');
	}
	const initResult: UploadInitResponse = initEnvelope;

	if (initResult.alreadyExists && initResult.record) {
		return initResult.record;
	}

	await putSignedUpload(initResult.uploadUrl, file, contentType);

	const finalizeEnvelope: UploadFunctionEnvelope = await postJson(
		`${endpointBase}/uploadMediaFinalize`,
		{ sessionId: initResult.sessionId },
		'finalize',
		apiKey || undefined,
	);
	if (!isUploadFinalizeBody(finalizeEnvelope)) {
		throw new UploadMediaError('INVALID_RESPONSE', 'finalize', 'Upload finalize returned no record');
	}

	return finalizeEnvelope.record;
};
